// src/utilities/facebook.utility.ts

interface FacebookPixel {
  (...args: any[]): void;
  callMethod?: (...args: any[]) => void;
  queue?: any[];
  push?: (...args: any[]) => void;
  loaded?: boolean;
  version?: string;
}

declare global {
  interface Window {
    fbq?: FacebookPixel;
    _fbq?: FacebookPixel;
  }
}

const FACEBOOK_PIXEL_ID = "2780211435649751";

/**
 * Initializes Facebook Pixel
 */
export function initFacebookPixel() {
  if (typeof window === "undefined") {
    return;
  }

  // Check if fbq is already initialized
  if (typeof window.fbq === "function") {
    return;
  }

  // Initialize fbq function
  (function () {
    const fbq: FacebookPixel = function (...args: any[]) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args);
      } else if (fbq.queue) {
        fbq.queue.push(args);
      }
    };

    if (!window.fbq) window.fbq = fbq;
    if (fbq.push) fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];

    if (!window._fbq) window._fbq = fbq;

    // Create and insert the script tag
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    const firstScript = document.getElementsByTagName("script")[0];
    firstScript.parentNode?.insertBefore(script, firstScript);

    // Initialize and track page view
    if (window.fbq) {
      window.fbq("init", FACEBOOK_PIXEL_ID);
      window.fbq("track", "PageView");
    }
  })();
}

/**
 * Track a Facebook Pixel standard event
 * @param eventName - Standard event name (e.g., 'Lead', 'Contact', 'Schedule')
 * @param parameters - Optional event parameters (value, currency, etc.)
 */
export function trackFacebookEvent(
  eventName: string,
  parameters?: {
    value?: number;
    currency?: string;
    [key: string]: any;
  }
) {
  if (typeof window === "undefined" || typeof window.fbq !== "function") {
    console.warn("Facebook Pixel not initialized");
    return;
  }

  try {
    if (parameters) {
      window.fbq("track", eventName, parameters);
    } else {
      window.fbq("track", eventName);
    }
  } catch (error) {
    console.error("Error tracking Facebook event:", error);
  }
}

/**
 * Track a Facebook Pixel custom event
 * @param eventName - Custom event name (e.g., 'DEMO_BOOKED')
 * @param parameters - Optional event parameters (value, currency, etc.)
 */
export function trackFacebookCustomEvent(
  eventName: string,
  parameters?: {
    value?: number;
    currency?: string;
    [key: string]: any;
  }
) {
  if (typeof window === "undefined" || typeof window.fbq !== "function") {
    console.warn("Facebook Pixel not initialized");
    return;
  }

  try {
    if (parameters) {
      window.fbq("trackCustom", eventName, parameters);
    } else {
      window.fbq("trackCustom", eventName);
    }
  } catch (error) {
    console.error("Error tracking Facebook custom event:", error);
  }
}

/**
 * Track a Lead event (consultation booking form submission)
 */
export function trackLeadEvent(parameters?: {
  value?: number;
  currency?: string;
  [key: string]: any;
}) {
  trackFacebookEvent("Lead", parameters);
}

/**
 * Track an InitiateCheckout event (user starts the paid booking flow)
 */
export function trackInitiateCheckoutEvent(parameters?: {
  value?: number;
  currency?: string;
  [key: string]: any;
}) {
  trackFacebookEvent("InitiateCheckout", parameters);
}

/**
 * Track a Contact event (user initiates contact)
 */
export function trackContactEvent() {
  trackFacebookEvent("Contact");
}

/**
 * Track a DEMO_BOOKED custom event (consultation/demo booked successfully)
 */
export function trackDemoBookedEvent(parameters?: {
  value?: number;
  currency?: string;
  [key: string]: any;
}) {
  trackFacebookCustomEvent("DEMO_BOOKED", parameters);
}

/**
 * Track a Purchase event (paid booking completed successfully)
 */
export function trackPurchaseEvent(parameters?: {
  value?: number;
  currency?: string;
  content_name?: string;
  content_type?: string;
  [key: string]: any;
}) {
  trackFacebookEvent("Purchase", parameters);
}

/**
 * Track ViewContent event (page/content viewed)
 */
export function trackViewContentEvent(parameters?: {
  content_name?: string;
  content_type?: string;
  value?: number;
  currency?: string;
  [key: string]: any;
}) {
  trackFacebookEvent("ViewContent", parameters);
}
